import React, { useEffect, useState, useCallback } from 'react';
import {
  Input,
  Layout,
  List,
  Button,
  Typography,
  message as antdMessage,
  Tag
} from 'antd';
import { WhatsAppOutlined, SendOutlined } from '@ant-design/icons';
import { Api_VariavelGlobal } from '../global';

const { Sider, Content } = Layout;
const { Text } = Typography;

// Normaliza o telefone removendo caracteres especiais e prefixo 55
const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '').replace(/^55/, '');
};

const ClientMessages = () => {
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  // 🔹 Buscar clientes
  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/users`);
      const data = await res.json();

      const formatted = data.map(u => ({
        key: u.ID_USUARIO,
        name: u.LOGIN,
        telefone: u.TELEFONE,
        status: u.STATUS
      }));

      setClients(formatted);
      setFiltered(formatted);
    } catch {
      antdMessage.error('Erro ao carregar clientes');
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // 🔍 Filtrar clientes
  useEffect(() => {
    const s = search.toLowerCase();
    setFiltered(
      clients.filter(c =>
        c.name.toLowerCase().includes(s) ||
        (c.telefone || '').includes(s)
      )
    );
  }, [search, clients]);

  // 🔹 Selecionar cliente e carregar mensagens
  const selectClient = async (client) => {
    if (!client.telefone) {
      return antdMessage.warning('Cliente sem telefone');
    }

    const phone = normalizePhone(client.telefone);
    setSelectedClient({ ...client, telefone: phone });
    setMessages([]);

    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/twilio/whatsapp/messages/${phone}`);
      if (!res.ok) return;

      const data = await res.json();
      setMessages(data || []);
    } catch {
      antdMessage.error('Erro ao carregar mensagens');
    }
  };

  // 🔹 Enviar mensagem
  const sendMessage = async () => {
    if (!text.trim() || !selectedClient) return;

    const newMessage = {
      body: text,
      direction: 'out',
      createdAt: new Date().toISOString()
    };

    // Aparece instantaneamente
    setMessages(prev => [...prev, newMessage]);
    setText('');
    setLoading(true);

    try {
      await fetch(`${Api_VariavelGlobal}/api/twilio/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: selectedClient.telefone,
          message: newMessage.body
        })
      });
    } catch {
      antdMessage.error('Erro ao enviar mensagem');
      // Remove se falhar
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ height: '100vh', background: '#f0f2f5', marginTop: '60px' }}>
      {/* 🧑 CLIENTES */}
      <Sider width={320} style={{ background: '#fff', borderRight: '1px solid #ddd', padding: '20px' }}>
        <Input.Search
          placeholder="Buscar cliente"
          allowClear
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        <List
          dataSource={filtered}
          renderItem={item => (
            <List.Item
              onClick={() => selectClient(item)}
              style={{
                cursor: 'pointer',
                background: selectedClient?.key === item.key ? '#e6f4ff' : ''
              }}
            >
              <List.Item.Meta
                title={
                  <>
                    {item.name}{' '}
                    {item.status === 1 && <Tag color="green">Ativo</Tag>}
                  </>
                }
                description={item.telefone || 'Sem telefone'}
              />
              <WhatsAppOutlined style={{ color: '#25D366', fontSize: 18 }} />
            </List.Item>
          )}
        />
      </Sider>

      {/* 💬 CHAT */}
      <Content style={{ display: 'flex', flexDirection: 'column' }}>
        {selectedClient ? (
          <>
            {/* Header */}
            <div style={{ padding: 16, background: '#075E54', color: '#fff', fontWeight: 500 }}>
              {selectedClient.name} — {selectedClient.telefone}
            </div>

            {/* Mensagens */}
            <div style={{ flex: 1, padding: 16, overflowY: 'auto', background: '#ece5dd' }}>
              {messages.map((msg, idx) => (
                <div
                  key={msg.sid || msg.id || `${msg.createdAt}-${idx}`}
                  style={{
                    display: 'flex',
                    justifyContent:
                      msg.direction === 'out' || msg.direction === 'sent'
                        ? 'flex-end'
                        : 'flex-start',
                    marginBottom: 8
                  }}
                >
                  <div
                    style={{
                      background: msg.direction === 'out' ? '#dcf8c6' : '#fff',
                      padding: '8px 12px',
                      borderRadius: 8,
                      maxWidth: '70%'
                    }}
                  >
                    <Text>{msg.body}</Text>
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div style={{ padding: 12, borderTop: '1px solid #ddd', display: 'flex', gap: 8 }}>
              <Input.TextArea
                rows={2}
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Digite uma mensagem"
              />
              <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={sendMessage} />
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
            Selecione um cliente para iniciar a conversa
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default ClientMessages;
